import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps } from '@mui/material/styles';
import { Trans } from 'react-i18next';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';

import {
    IContentTypeValue,
    IExtraCategoryFragment,
    IExtraCategoryOption,
    ExtraCategoriesDocument,
} from 'graphql/types';
import { TExtraCategoryInput } from 'organisation/types';
import { getExtraCategorySelectOptionValue } from 'organisation/utils/extraCategory';
import { Checkbox } from 'common/components/Checkbox';
import { useApolloError } from 'common/hooks/useApolloError';
import { TControllerProps, TSelectOption } from 'common/types';
import { Link } from 'common/components/Link';
import {
    AutocompleteField,
    DatePickerField,
    MultiSelectField,
    TextField,
} from 'common/forms/Field';

interface IProps {
    contentType: IContentTypeValue;
    name: string;
    emptyMessageSx?: SxProps;
}

export const ExtraCategoryFormFields = <TFieldValues extends FieldValues>({
    contentType,
    name,
    emptyMessageSx,
}: IProps & TControllerProps<TFieldValues>) => {
    const { showApolloError } = useApolloError();
    const { control } = useFormContext<{
        [name: string]: TExtraCategoryInput;
    }>();
    const { data, loading, error } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType },
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);
    const { extraCategories } = data || {};

    return (
        <>
            {!loading && !extraCategories?.length && (
                <Box sx={emptyMessageSx}>
                    <Trans
                        components={{
                            a: (
                                <Link
                                    rel="noreferrer"
                                    to="ORGANISATION_EXTRA_CATEGORIES"
                                    underline="always"
                                />
                            ),
                        }}
                        i18nKey="noExtraCategories"
                    />
                </Box>
            )}

            {extraCategories?.map((extraCategory: IExtraCategoryFragment) => {
                const {
                    id,
                    multipleChoices,
                    name: extraCategoryName,
                    choiceOptions,
                } = extraCategory;

                switch (extraCategory.categoryType) {
                    case IExtraCategoryOption.Choice:
                        return (
                            <Controller
                                control={control}
                                key={`${name}.${id}`}
                                name={`${name}.${id}`}
                                render={({ field, fieldState: { error } }) => {
                                    const options = choiceOptions?.map(
                                        (choiceOption): TSelectOption => ({
                                            label: choiceOption.stringValue,
                                            value: choiceOption.id,
                                        })
                                    ) as TSelectOption[];

                                    const props = {
                                        InputProps: {
                                            label: extraCategoryName,
                                        },
                                        name: `${name}.${id}`,
                                        options,
                                    };

                                    if (multipleChoices) {
                                        return (
                                            <MultiSelectField
                                                {...field}
                                                {...props}
                                                error={!!error}
                                                getOptionLabel={(option) =>
                                                    (option as TSelectOption)
                                                        .label
                                                }
                                                helperText={error?.message}
                                                isOptionEqualToValue={(
                                                    option,
                                                    value
                                                ) =>
                                                    (option as TSelectOption)
                                                        .value ===
                                                    (value as TSelectOption)
                                                        .value
                                                }
                                                options={options}
                                                onChange={(_e, value) => {
                                                    field.onChange(value);
                                                }}
                                            />
                                        );
                                    }

                                    return (
                                        <AutocompleteField<TSelectOption>
                                            {...field}
                                            {...props}
                                            error={!!error}
                                            getOptionLabel={(option) =>
                                                option.label
                                            }
                                            helperText={error?.message}
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) => option.value === value.value}
                                            options={options}
                                            value={getExtraCategorySelectOptionValue(
                                                field.value
                                            )}
                                            onChange={(_e, value) => {
                                                field.onChange(value);
                                            }}
                                        />
                                    );
                                }}
                            />
                        );

                    case IExtraCategoryOption.Boolean:
                        return (
                            <Box key={`${name}.${id}`}>
                                <Controller
                                    control={control}
                                    name={`${name}.${id}`}
                                    render={({
                                        field,
                                        fieldState: { error },
                                    }) => (
                                        <>
                                            <FormControlLabel
                                                {...field}
                                                checked={field.value as boolean}
                                                control={
                                                    <Checkbox disableRipple />
                                                }
                                                label={extraCategoryName}
                                                name={`${name}.${id}`}
                                            />

                                            {!!error && (
                                                <FormHelperText error>
                                                    {error.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </Box>
                        );

                    case IExtraCategoryOption.Datetime:
                        return (
                            <Controller
                                control={control}
                                key={`${name}.${id}`}
                                name={`${name}.${id}`}
                                render={({ field: { value, ...field } }) => (
                                    <DatePickerField
                                        label={extraCategoryName}
                                        value={value as DateTime}
                                        {...field}
                                    />
                                )}
                            />
                        );

                    default:
                        return (
                            <Controller
                                control={control}
                                key={`${name}.${id}`}
                                name={`${name}.${id}`}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        label={extraCategoryName}
                                        {...field}
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        );
                }
            })}
        </>
    );
};
